import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  User,
  Gift,
  Wallet,
  Users,
  Copy,
  Share2,
  MessageCircle,
  IndianRupee,
  CheckCircle
} from "lucide-react";

export default function ProfilePage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token) {
      fetchReferralStats();
    }
  }, [token]);

  const fetchReferralStats = async () => {
    try {
      const response = await axios.get(`${API}/referrals/my-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralStats(response.data);
    } catch (error) {
      console.error("Failed to fetch referral stats:", error);
    }
    setLoading(false);
  };

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success("कोड कॉपी हो गया!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOnWhatsApp = () => {
    const referralLink = `${window.location.origin}/auth?ref=${user?.referral_code}`;
    const message = `🎉 *उद्यमी मित्र* पर जुड़ें और ₹25 बोनस पाएं!

मैं उद्यमी मित्र का उपयोग करता/करती हूं - भारत का #1 बिज़नेस प्रोजेक्ट पोर्टल!

✅ 120+ बिज़नेस आइडियाज
✅ AI-powered प्रोजेक्ट रिपोर्ट
✅ मशीनरी और सप्लायर की जानकारी

🎁 मेरा रेफरल कोड: *${user?.referral_code}*

👉 अभी रजिस्टर करें: ${referralLink}

आपको ₹25 का वेलकम बोनस मिलेगा! 🚀`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Skeleton className="h-32 bg-white mb-4" />
          <Skeleton className="h-48 bg-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-orange-500" />
            </div>
            <div>
              <h1 className="font-rajdhani font-bold text-2xl text-gray-800">
                {user?.name}
              </h1>
              <p className="text-gray-8000">{user?.email}</p>
              <p className="text-gray-8000">{user?.phone}</p>
            </div>
          </div>
        </div>

        {/* Wallet & Stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <Wallet className="w-6 h-6 text-emerald-500 mb-2" />
            <p className="text-xs text-gray-8000 mb-1">वॉलेट बैलेंस</p>
            <p className="font-rajdhani font-bold text-2xl text-emerald-500 flex items-center">
              <IndianRupee className="w-5 h-5" />
              {referralStats?.wallet_balance || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <Users className="w-6 h-6 text-blue-500 mb-2" />
            <p className="text-xs text-gray-8000 mb-1">कुल रेफरल्स</p>
            <p className="font-rajdhani font-bold text-2xl text-gray-800">
              {referralStats?.total_referrals || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <Gift className="w-6 h-6 text-orange-500 mb-2" />
            <p className="text-xs text-gray-8000 mb-1">प्रति रेफरल बोनस</p>
            <p className="font-rajdhani font-bold text-2xl text-orange-500 flex items-center">
              <IndianRupee className="w-5 h-5" />
              {referralStats?.bonus_per_referral || 50}
            </p>
          </div>
        </div>

        {/* Referral Section */}
        <div className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 rounded-xl border border-orange-500/30 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Gift className="w-8 h-8 text-orange-500" />
            <div>
              <h2 className="font-rajdhani font-bold text-xl text-gray-800">
                दोस्तों को रेफर करें, ₹50 कमाएं!
              </h2>
              <p className="text-sm text-gray-500">
                हर successful referral पर आपको ₹50 और आपके दोस्त को ₹25 मिलेगा
              </p>
            </div>
          </div>

          {/* Referral Code */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-xs text-gray-8000 mb-2">आपका रेफरल कोड</p>
            <div className="flex items-center gap-3">
              <code className="flex-1 text-2xl font-mono font-bold text-orange-500 tracking-widest">
                {user?.referral_code || "---"}
              </code>
              <Button
                onClick={copyReferralCode}
                variant="outline"
                className={`btn-secondary rounded-sm ${copied ? 'border-emerald-500 text-emerald-500' : ''}`}
              >
                {copied ? (
                  <CheckCircle className="w-4 h-4 mr-2" />
                ) : (
                  <Copy className="w-4 h-4 mr-2" />
                )}
                {copied ? "कॉपी हो गया" : "कोड कॉपी करें"}
              </Button>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={shareOnWhatsApp}
              className="flex-1 bg-green-600 hover:bg-green-700 rounded-sm"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp पर शेयर करें
            </Button>
            <Button
              onClick={() => {
                const link = `${window.location.origin}/auth?ref=${user?.referral_code}`;
                navigator.clipboard.writeText(link);
                toast.success("लिंक कॉपी हो गया!");
              }}
              variant="outline"
              className="btn-secondary rounded-sm"
            >
              <Share2 className="w-4 h-4 mr-2" />
              लिंक कॉपी
            </Button>
          </div>
        </div>

        {/* Referral History */}
        {referralStats?.referral_history && referralStats.referral_history.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-rajdhani font-bold text-lg text-gray-800 mb-4">
              रेफरल हिस्ट्री
            </h3>
            <div className="space-y-3">
              {referralStats.referral_history.map((ref) => (
                <div
                  key={ref.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">{ref.referred_name}</p>
                      <p className="text-xs text-gray-8000">
                        {new Date(ref.created_at).toLocaleDateString('hi-IN')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-500 font-bold">+₹{ref.referrer_bonus}</p>
                    <p className="text-xs text-gray-8000">बोनस मिला</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!referralStats?.referral_history || referralStats.referral_history.length === 0) && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Users className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-lg text-gray-500 mb-2">अभी तक कोई रेफरल नहीं</h3>
            <p className="text-gray-8000 mb-4">
              अपने दोस्तों को शेयर करें और ₹50 प्रति रेफरल कमाएं!
            </p>
            <Button
              onClick={shareOnWhatsApp}
              className="bg-green-600 hover:bg-green-700 rounded-sm"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              अभी शेयर करें
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
